import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Grid, List, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';
import { useProductPrices } from '../hooks/useProductPrices';
import { ProductCard } from '../components/product/ProductCard';

interface Product {
  id: string;
  name: string;
  description: string;
  base_price: number;
  main_image: string;
  stock: number;
  min_order: number;
  status: 'active' | 'inactive' | 'out_of_stock';
  category: {
    name: string;
    slug: string;
  };
}

interface ProductResponse {
  id: string;
  name: string;
  description: string;
  base_price: number;
  main_image: string;
  stock: number;
  min_order: number;
  status: 'active' | 'inactive' | 'out_of_stock';
  category: Array<{
    name: string;
    slug: string;
  }>;
}

interface CollectionProductResponse {
  order: number;
  product: ProductResponse;
}

interface Collection {
  id: string;
  name: string;
  description: string | null;
  type: 'category_mix' | 'new' | 'featured' | 'sale';
  image: string | null;
  order: number;
  is_active: boolean;
  category_ids: string[] | null;
  created_at: string;
  updated_at: string | null;
}

export default function SeeMoreCollection() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');

  // Usar hook centralizado de precios
  const productIds = useMemo(() => products.map(p => p.id), [products]);
  const { prices, loading: pricesLoading } = useProductPrices(productIds, {
    enabled: productIds.length > 0
  });

  useEffect(() => {
    if (id) {
      fetchCollectionAndProducts();
    }
  }, [id]);

  const fetchCollectionAndProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener la colección por ID
      const { data: collection, error: collectionError } = await supabase
        .from('product_collections')
        .select(`
          id,
          name,
          description,
          type,
          image,
          order,
          is_active,
          category_ids,
          created_at,
          updated_at
        `)
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (collectionError) {
        if (collectionError.code === 'PGRST116') {
          throw new Error('Colección no encontrada');
        }
        throw collectionError;
      }

      if (!collection) {
        throw new Error('Colección no encontrada');
      }

      setCollection(collection);

      // Obtener los productos de la colección de forma simplificada
      const { data: collectionProducts, error: collectionProductsError } = await supabase
        .from('collection_products')
        .select('product_id, order')
        .eq('collection_id', id)
        .order('order', { ascending: true });

      if (collectionProductsError) throw collectionProductsError;

      if (!collectionProducts || collectionProducts.length === 0) {
        setProducts([]);
        return;
      }

      // Obtener los productos por separado
      const productIds = collectionProducts.map(cp => cp.product_id);
      
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select(`
          id,
          name,
          description,
          base_price,
          main_image,
          stock,
          min_order,
          status,
          category_id
        `)
        .in('id', productIds)
        .eq('status', 'active');

      if (productsError) throw productsError;

      // Obtener las categorías por separado
      const categoryIds = productsData?.map(p => p.category_id).filter(Boolean) || [];
      const { data: categoriesData } = await supabase
        .from('categories')
        .select('id, name, slug')
        .in('id', categoryIds);

      // Crear un mapa de categorías
      const categoriesMap = categoriesData?.reduce((acc, cat) => {
        acc[cat.id] = cat;
        return acc;
      }, {} as Record<string, any>) || {};

      // Combinar productos con categorías y ordenar según collection_products
      const transformedProducts = productsData?.map(product => ({
        ...product,
        category: categoriesMap[product.category_id] || { name: '', slug: '' },
        order: collectionProducts.find(cp => cp.product_id === product.id)?.order || 0
      }))
      .sort((a, b) => a.order - b.order) || [];

      setProducts(transformedProducts as Product[]);
    } catch (error) {
      console.error('Error fetching collection:', error);
      setError(error instanceof Error ? error.message : 'Error al cargar la colección');
      setCollection(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="flex justify-between items-center">
            <div className="h-10 bg-gray-200 rounded w-1/3"></div>
            <div className="h-8 bg-gray-200 rounded w-24"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-gray-100 rounded-xl overflow-hidden">
                <div className="h-40 bg-gray-200"></div>
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="flex justify-between">
                    <div className="h-5 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-5 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!collection || error) {
    return (
      <>
        <SEO
          title="Colección no encontrada | Disolgua"
          description="La colección que buscas no existe o no está disponible"
          url={`/see-more/${id}`}
          type="website"
        />
        
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {error || 'Colección no encontrada'}
            </h2>
            <p className="text-gray-600 mb-8">
              La colección que buscas no existe o no está disponible en este momento.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Volver atrás
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ir al inicio
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${collection.name} | Disolgua`}
        description={collection.description || `Explora nuestra colección ${collection.name} en Disolgua`}
        url={`/see-more/${collection.id}`}
        type="website"
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-bold text-gray-900">{collection.name}</h1>
            {collection.description && (
              <p className="text-gray-600">{collection.description}</p>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <div className="flex items-center gap-2 bg-white rounded-lg border p-1">
              <button
                onClick={() => setView('grid')}
                className={`px-3 py-1.5 rounded flex items-center gap-2 ${
                  view === 'grid'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Grid className="w-4 h-4" />
                Cuadrícula
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-3 py-1.5 rounded flex items-center gap-2 ${
                  view === 'list'
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <List className="w-4 h-4" />
                Lista
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid/List */}
        {pricesLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-gray-100 rounded-xl overflow-hidden animate-pulse">
                <div className="h-40 bg-gray-200"></div>
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-xl font-medium text-gray-700 mb-2">No hay productos en esta colección</h3>
            <p className="text-gray-500">Vuelve más tarde para ver nuevos productos</p>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {products.map((product) => {
              const priceData = prices[product.id];

              return (
                <ProductCard
                  key={product.id}
                  product={{
                    id: product.id,
                    name: product.name,
                    main_image: product.main_image,
                    stock: product.stock,
                    min_order: product.min_order,
                    category: product.category
                  }}
                  priceInfo={priceData ? {
                    price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                    originalPrice: priceData.isOnSale ? priceData.price : null,
                    isOnSale: priceData.isOnSale,
                    discountPercentage: priceData.discountPercentage
                  } : undefined}
                  variant="catalog"
                  showCategory={true}
                  showCode={false}
                  showAddToCart={true}
                />
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            {products.map((product) => {
              const priceData = prices[product.id];

              return (
                <ProductCard
                  key={product.id}
                  product={{
                    id: product.id,
                    name: product.name,
                    description: product.description,
                    main_image: product.main_image,
                    stock: product.stock,
                    min_order: product.min_order,
                    category: product.category
                  }}
                  priceInfo={priceData ? {
                    price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                    originalPrice: priceData.isOnSale ? priceData.price : null,
                    isOnSale: priceData.isOnSale,
                    discountPercentage: priceData.discountPercentage
                  } : undefined}
                  variant="list"
                  showCategory={true}
                  showCode={false}
                  showAddToCart={true}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
} 